import React, { useEffect, useState } from "react";
import axios from "axios";
import api from "../api";
import { Plus, FileText, Download, Trash2 } from "lucide-react";
import DocumentosCorporativosForm from "./DocumentosCorporativosForm";

export default function DocumentosCorporativos({ user }) {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filtro, setFiltro] = useState("");

  useEffect(() => {
    fetchDocumentos();
  }, []);

  const fetchDocumentos = async () => {
    try {
      const response = await axios.get(
        `/documentos-corporativos`
      );
      setDocumentos(response.data);
    } catch (error) {
      console.error("Error al cargar documentos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploaded = (nuevoDoc) => {
    console.log("[Depuración] Documento subido:", nuevoDoc);
    setDocumentos((prev) => [nuevoDoc, ...prev]);
    setShowForm(false);
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar este documento?")) return;

    try {
      await api.post(
        `/documentos-corporativos/destroy`,
        { id }
      );
      setDocumentos(
        documentos.filter((doc) => doc.id_documento_corporativo !== id)
      );
    } catch (error) {
      console.error("Error al eliminar documento:", error);
    }
  };

  // ✅ Función para descargar
  const handleDescargar = async (id) => {
    try {
      console.log(`Descargando documento con ID: ${id}`);
      const response = await axios.get(
        `/documentos-corporativos/${id}/descargar`,
        { responseType: "blob" }
      );
      console.log("Respuesta de descarga recibida (blob).");

      // Intentar obtener nombre desde Content-Disposition
      const disposition = response.headers["content-disposition"];
      let filename = "documento.pdf";
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) filename = match[1];
      }

      // Crear descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error descargando el documento:", error);
      alert("No se pudo descargar el documento.");
    }
  };

  const puedeSubir =
    user.rol.toLowerCase() === "administrador" ||
    (user.rol.toLowerCase() === "empleado" && user.departamento?.toLowerCase() === "recursos humanos");

  const documentosFiltrados = documentos.filter((doc) =>
    doc.nombre_documento.toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div className="relative p-6">
      <h2 className="text-2xl font-bold mb-4">📂 Documentos Corporativos</h2>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Buscar por nombre..."
          className="w-full p-2 border border-gray-300 rounded"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
      </div>

      {loading ? (
        <p className="text-gray-500">Cargando documentos...</p>
      ) : documentosFiltrados.length === 0 ? (
        <p className="text-gray-500">No hay documentos que coincidan con la búsqueda.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {documentosFiltrados.map((doc) => (
            <div
              key={doc.id_documento_corporativo}
              className="p-4 bg-cart shadow rounded-lg flex flex-col justify-between"
            >
              <div className="flex items-center gap-2">
                <FileText className="text-blue-500" />
                <h3 className="font-semibold">{doc.nombre_documento}</h3>
              </div>

              <p className="text-sm text-gray-600 mt-2">
                {doc.descripcion || "Sin descripción"}
              </p>

              <p className="text-xs text-gray-400 mt-2">
                Subido: {new Date(doc.fecha_subida).toLocaleDateString()}
              </p>

              <div className="flex justify-between items-center mt-3">
                <button
                  onClick={() =>
                    handleDescargar(doc.id_documento_corporativo, doc.nombre_documento)
                  }
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                >
                  <Download size={16} />
                </button>

                {puedeSubir && (
                  <button
                    onClick={() =>
                      handleEliminar(doc.id_documento_corporativo)
                    }
                    className="flex items-center gap-1 px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {puedeSubir && (
        <button
          className="fixed bottom-14 right-6 w-14 h-14 rounded-full bg-success text-white shadow-lg hover:bg-lime transition flex items-center justify-center"
          onClick={() => setShowForm(true)}
        >
          <Plus size={28} />
        </button>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg relative">
            <button
              className="absolute top-2 right-2 text-gray-600 hover:text-red-500"
              onClick={() => setShowForm(false)}
            >
              ✕
            </button>

            <DocumentosCorporativosForm
              onClose={() => setShowForm(false)}
              onUploaded={handleUploaded}
            />
          </div>
        </div>
      )}
    </div>
  );
}
